import { compareAlphanumeric } from 'utils/stringHelpers'

const all = new Map<string, GeckoAlgorithm>()

/**
 * There are many ways to derive thickness measurement from A-scans; this class
 * is an abstraction representing one of those ways.
 */
export class GeckoAlgorithm {
  //----------------------------------------------------------------------------

  public static readonly Description =
    'The algorithm used to derive thickness measurement'

  //----------------------------------------------------------------------------

  public static readonly PeakGateA = new GeckoAlgorithm(
    'peak-gate-a',
    'Gate A - Peak',
    'Distance from Sample 0 to the first prominent peak within the bounds of Gate A'
  )
  public static readonly PeakGateB = new GeckoAlgorithm(
    'peak-gate-b',
    'Gate B - Peak',
    'Distance from Sample 0 to the first prominent peak within the bounds of Gate B'
  )
  public static readonly PeakGate0 = new GeckoAlgorithm(
    'peak-gate-0',
    'Gate 0 - Peak',
    'Distance from Sample 0 to the first prominent peak within the bounds of Gate 0'
  )
  public static readonly PeakGate1 = new GeckoAlgorithm(
    'peak-gate-1',
    'Gate 1 - Peak',
    'Distance from "Gate 0 - Peak" point to the first prominent peak within the bounds of Gate 1'
  )
  public static readonly PeakGate2 = new GeckoAlgorithm(
    'peak-gate-2',
    'Gate 2 - Peak',
    'Distance from "Gate 1 - Peak" point to the first prominent peak within the bounds of Gate 2'
  )
  public static readonly FlankGateA = new GeckoAlgorithm(
    'flank-gate-a',
    'Gate A - Flank',
    'Distance from Sample 0 to the first sample crossing the threshold of Gate A'
  )
  public static readonly FlankGateB = new GeckoAlgorithm(
    'flank-gate-b',
    'Gate B - Flank',
    'Distance from Sample 0 to the first sample crossing the threshold of Gate B'
  )
  public static readonly FlankGate0 = new GeckoAlgorithm(
    'flank-gate-0',
    'Gate 0 - Flank',
    'Distance from Sample 0 to the first sample crossing the threshold of Gate 0'
  )
  public static readonly FlankGate1 = new GeckoAlgorithm(
    'flank-gate-1',
    'Gate 1 - Flank',
    'The distance from the "Gate 0 - Flank" point to the first sample crossing the threshold of Gate 1'
  )
  public static readonly FlankGate2 = new GeckoAlgorithm(
    'flank-gate-2',
    'Gate 2 - Flank',
    'The distance from the "Gate 1 - Flank" point to the first sample crossing the threshold of Gate 2'
  )
  public static readonly AmplitudeGateA = new GeckoAlgorithm(
    'amplitude-gate-a',
    'Gate A - Amplitude',
    'Highest sample under Gate A'
  )
  public static readonly AmplitudeGateB = new GeckoAlgorithm(
    'amplitude-gate-b',
    'Gate B - Amplitude',
    'Highest sample under Gate B'
  )
  public static readonly AmplitudeGate0 = new GeckoAlgorithm(
    'amplitude-gate-0',
    'Gate 0 - Amplitude',
    'Highest sample under the first gate'
  )
  public static readonly AmplitudeGate1 = new GeckoAlgorithm(
    'amplitude-gate-1',
    'Gate 1 - Amplitude',
    'Highest sample under the second gate'
  )
  public static readonly AmplitudeGate2 = new GeckoAlgorithm(
    'amplitude-gate-2',
    'Gate 2 - Amplitude',
    'Highest sample under the third gate'
  )
  public static readonly None = new GeckoAlgorithm(
    'no-algorithm',
    'None',
    'No algorithm applied'
  )

  //----------------------------------------------------------------------------

  /** A unique ID which should line up with Data Systems and even Portal */
  public readonly id: string

  /** A user-facing name for this object, or its ID if none is populated */
  public readonly name: string

  /** A user-facing description for this a specific data algorithm */
  public readonly description: string

  private constructor(id: string, name: string, description: string) {
    this.id = id
    this.name = name
    this.description = description
    all.set(id, this)
  }

  //----------------------------------------------------------------------------

  /**
   * The default sort (alphabetical) for algorithm objects
   * @param a The first algorithm object
   * @param b The second algorithm object
   */
  public static DefaultSort(a: GeckoAlgorithm, b: GeckoAlgorithm): number {
    return compareAlphanumeric(a.name, b.name)
  }

  /**
   * Returns the unique algorithm instance for a given ID if it exists
   * @param id The unique ID of this algorithm
   */
  public static GetByID(id: string | undefined): GeckoAlgorithm | undefined {
    return id === undefined ? this.None : all.get(id)
  }

  /**
   * Returns the unique algorithm instance for a given ID, or creates a
   * unique algorithm instance from scratch for this new ID
   * @param id The unique ID of this object
   */
  public static GetOrCreate(id: string | undefined): GeckoAlgorithm {
    return id === undefined
      ? this.None
      : (all.get(id) ?? new GeckoAlgorithm(id, id, ''))
  }
}
